import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { Card } from 'reactstrap';

import CallexLoader from '../../../components/CallexLoader';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import AddEditBotSipRoutingModal from './AddEditBotSipRoutingModal';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  botSipRoutingDelete,
  botSipRoutingListGet,
  useBotSipRoutingList
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const PERMISSION_KEY = 'Bot SIP Routing';

const BotSipRouting = () => {
  const dispatch = useDispatch();
  const botSipRoutingList = useBotSipRoutingList();
  const userRolePermission = useUserRolePermission();

  const [isLoading, setIsLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchBotSipRoutingList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(botSipRoutingListGet({ ...filter })).unwrap();
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchBotSipRoutingList();
  }, [filter]);

  const deleteBotSipRouting = async () => {
    setIsDeleting(true);
    try {
      const response = await dispatch(botSipRoutingDelete(isDeleteId)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'Record deleted successfully');
        if (botSipRoutingList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchBotSipRoutingList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Bot SIP Routing"
        isExpandSearch
        searchPlaceholder="Search Bot SIP Routing"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
        isAddDisabled={!checkModulePermission('is_add', PERMISSION_KEY, userRolePermission)}
        isAdd
        addBtnName="Add Bot SIP Routing"
        onAddClick={() => setOpenAddModal(true)}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : botSipRoutingList?.data?.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Bot</th>
                  <th>Allow IP</th>
                  <th>Prefix</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {botSipRoutingList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.bot_name || val?.bot_id || '-'}</td>
                      <td>{val?.allow_ip || val?.allow_ip_id || '-'}</td>
                      <td>{val?.prefix || '-'}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', PERMISSION_KEY, userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', PERMISSION_KEY, userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', PERMISSION_KEY, userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    PERMISSION_KEY,
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={botSipRoutingList?.total}
              limit={botSipRoutingList?.limit}
              page={botSipRoutingList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchBotSipRoutingList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteBotSipRouting()}
        isLoading={isDeleting}
      />
      <AddEditBotSipRoutingModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchBotSipRoutingList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default BotSipRouting;
